import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Chip } from "../primitives/Chip";
import type { UiTheme } from "../theme";
import { PERIOD_OPTIONS } from "./progressWindow";

// PG-1 period selector — the single-select window pills atop the trend block.
// One <Chip pill> per preset (7/14/30/90 d); the shared Chip already carries
// the ≥44 touch target + the non-colour-only "✓ " selected cue, so the a11y
// floor is met without re-rolling a toggle here. Drives Strength / Rhythm /
// the "More detail" panels (Coverage stays weekly).
export function PeriodSelector({
  theme,
  value,
  onChange,
}: {
  theme: UiTheme;
  value: number;
  onChange: (days: number) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const label = t("progress.period.selectorLabel", {
    defaultValue: "Analysis window",
  });
  return (
    <View style={styles.wrap}>
      {/* Visible caption: without it the pills read as loose floating text and
          the reader can't tell what they control (the label was screen-reader
          only before). Small-caps, matching the panel cardTitle treatment. */}
      <Text style={styles.caption}>{label}</Text>
      <View
        style={styles.row}
        accessibilityRole="radiogroup"
        accessibilityLabel={label}
      >
        {PERIOD_OPTIONS.map((days) => (
          <Chip
            key={days}
            theme={theme}
            role="radio"
            label={t("progress.period.days", { defaultValue: "{{days}} d", days })}
            selected={value === days}
            onPress={() => onChange(days)}
            // Vertical-only touch expansion: the default 8-all-sides hitSlop
            // would overlap the 4px gap between adjacent pills (same fix as the
            // Wave-2 chip rows). Vertical stays generous so each pill clears ≥44.
            hitSlop={{ top: 8, bottom: 8 }}
            style={styles.chip}
          />
        ))}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s, palette: p, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { marginBottom: s.s3, gap: s.s2 },
    caption: {
      // Matches the screen's sibling micro-label (statLabel): uppercase xs/700,
      // letterSpacing 0.6. ink3 (not statLabel's ink2) so it clears AA 4.5:1 on
      // bg in every theme — verified light+dark, catppuccin+rosé-pine.
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.6,
    },
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
    },
    // Let the pills size to their label; keep them from stretching full-width.
    chip: { flexGrow: 0 },
  });
}
